/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Divider, Form, Grid, Header } from '@mairistem/composants';

import type { Adresse, Individu, ListeRecensement } from '@/types';
import { unformatPhoneNumber } from '@/utils';
import PanelButtonEdition from '../PanelButtonEdition';
import AdresseRattachementModal from './AdresseRattachementModal';
import InformationContactModal from './InformationContactModal';

interface AdressesContactsPanelProps {
  tel1: string;
  tel2: string;
  mail: string;
  adresseRattachement: Partial<Adresse>;
  adresseContact: Partial<Adresse>;
  isResideCommune: boolean;
  recensement: ListeRecensement;
  recense: Partial<Individu>;
  updated: boolean;
  onChange: (data: Partial<Individu>) => void;
  onSave: () => Promise<void>;
  onRollback: () => void;
}

const AdressesContactsPanel = (props: AdressesContactsPanelProps) => {
  const {
    tel1,
    tel2,
    mail,
    adresseRattachement,
    adresseContact,
    isResideCommune,
    recensement,
    recense,
    updated,
    onChange,
    onSave,
    onRollback,
  } = props;

  const [modalAdresseRattachementOpen, setModalAdresseRattachementOpen] =
    React.useState<boolean>(false);
  const [modalInformationContactOpen, setModalInformationContactOpen] =
    React.useState<boolean>(false);

  const handleCloseAdresseRattachement = React.useCallback(() => {
    setModalAdresseRattachementOpen(false);
  }, []);

  const handleCloseInformationContact = React.useCallback(() => {
    setModalInformationContactOpen(false);
  }, []);

  const handleCancelAdresseRattachement = React.useCallback(() => {
    onRollback();
    handleCloseAdresseRattachement();
  }, [handleCloseAdresseRattachement, onRollback]);

  const handleCancelInformationContact = React.useCallback(() => {
    onRollback();
    handleCloseInformationContact();
  }, [handleCloseInformationContact, onRollback]);

  // Formatage du département pour l'affichage
  const formatDepartement = (adresse: Partial<Adresse> | undefined) => {
    const code = adresse?.codeDepartement || '';
    const libelle = adresse?.libelleDepartement || '';
    return `${code}${!_.isEmpty(libelle) ? ` (${libelle})` : ''}`;
  };

  return (
    <Grid as={Form}>
      <Grid.Row>
        <Header as="h5" color="blue" style={{ margin: 0 }}>
          ADRESSE DE RATTACHEMENT
        </Header>
        <PanelButtonEdition
          onClick={() => {
            setModalAdresseRattachementOpen(true);
          }}
          data-testid="panel-edit-modal-adresse-rattachement"
        />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={5}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Réside dans la commune : </b>
          </span>
          <span>{recense?.isResideCommune ? 'Oui' : 'Non'}</span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={5}
          largeScreen={6}
          widescreen={4}
        >
          <span>
            <b>Département : </b>
          </span>
          <span>{formatDepartement(recense?.adresseRattachement)}</span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={5}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Commune (Pays) : </b>
          </span>
          <span>
            {`${recense?.adresseRattachement?.libelleCodePostal || ''} ${recense?.adresseRattachement?.libelleCommune || ''} ${
              recense?.adresseRattachement?.libellePays
                ? ` (${recense.adresseRattachement.libellePays})`
                : ''
            }`}
          </span>
        </Grid.Column>
        {modalAdresseRattachementOpen && (
          <AdresseRattachementModal
            adresseRattachement={adresseRattachement}
            isResideCommune={isResideCommune}
            recensement={recensement}
            updated={updated}
            onChange={onChange}
            onSave={onSave}
            onClose={handleCloseAdresseRattachement}
            onCancel={handleCancelAdresseRattachement}
          />
        )}
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <Header as="h5" color="blue" style={{ margin: 0 }}>
          INFORMATIONS DE CONTACT
        </Header>
        <PanelButtonEdition
          onClick={() => {
            setModalInformationContactOpen(true);
          }}
          data-testid="panel-edit-modal-adresse-contact"
        />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column
          mobile={16}
          tablet={6}
          computer={8}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Département : </b>
          </span>
          <span>{formatDepartement(recense?.adresseContact)}</span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={10}
          computer={8}
          largeScreen={6}
          widescreen={4}
        >
          <span>
            <b>Commune (Pays) : </b>
          </span>
          <span>
            {`${recense?.adresseContact?.libelleCodePostal || ''} ${recense?.adresseContact?.libelleCommune || ''} ${
              recense?.adresseContact?.libellePays
                ? ` (${recense.adresseContact.libellePays})`
                : ''
            }`}
          </span>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={5}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Téléphone 1 : </b>
          </span>
          <span>
            {recense?.tel1 && recense.tel1 !== '0' && recense.tel1 !== '00'
              ? unformatPhoneNumber(recense.tel1)
              : 'Non renseigné'}
          </span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={5}
          largeScreen={6}
          widescreen={4}
        >
          <span>
            <b>Téléphone 2 : </b>
          </span>
          <span>
            {recense?.tel2 && recense.tel2 !== '0' && recense.tel2 !== '00'
              ? unformatPhoneNumber(recense.tel2)
              : 'Non renseigné'}
          </span>
        </Grid.Column>
        <Grid.Column
          mobile={16}
          tablet={5}
          computer={5}
          largeScreen={5}
          widescreen={4}
        >
          <span>
            <b>Mail : </b>
          </span>
          <span>
            {recense?.mail && recense.mail !== '0' && recense.mail !== '00'
              ? recense.mail
              : ''}
          </span>
        </Grid.Column>
        {modalInformationContactOpen && (
          <InformationContactModal
            adresseContact={adresseContact}
            adresseRattachement={adresseRattachement}
            tel1={tel1}
            tel2={tel2}
            mail={mail}
            recensement={recensement}
            updated={updated}
            onChange={onChange}
            onSave={onSave}
            onClose={handleCloseInformationContact}
            onCancel={handleCancelInformationContact}
          />
        )}
      </Grid.Row>
    </Grid>
  );
};

export default AdressesContactsPanel;
